import { Helmet } from "react-helmet";
import SignupFlowContainer from "../SignUpContainer/SignUpContainer";
import ProgressBar from "../ProgressBar";
import { cn } from "../../../../utils/css";
import Logo from "../../../../assets/img/pp-logo-primary.svg";
import BackgroundPattern from "../../../../assets/img/signup-bg-pattern.png";
import useTailwindBreakpoint from "../../../../hooks/useTailwindBreakpoint";
import { isProd } from "../../../../env";
import { useMachine } from "@xstate/react";
import { signupMachine as signupMachineControl } from "../../machines/signup.control.machine";
import { SignUpMachineContext } from "../../context/SignUpMachineContext";
import { useExperiment } from "../../../../hooks/useExperiment";
import { useEffect, useState } from "react";
import {
  getAttributionFromUrl,
  getAttributionFromCookies,
  setUtmCookies,
} from "../../../../utils/marketing-attribution";

const MobileWrapper = ({
  children,
  progressBar,
}: {
  children: React.ReactNode;
  progressBar: React.ReactNode;
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // this is used to fade in the container,
    // to help prevent layout shift when running an experiment which often causes a flicker
    // when the variant loads
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="tw-min-h-screen tw-flex tw-flex-col tw-items-center tw-justify-start md:tw-justify-center tw-w-full tw-bg-white">
      <div className="tw-w-full tw-px-9 tw-flex tw-flex-col tw-gap-4 tw-items-center tw-justify-start tw-p-4 tw-pt-8">
        <Logo className="tw-h-8" />
        <div className="tw-w-full tw-max-w-md tw-mb-4">{progressBar}</div>
        <div
          className={cn(
            "tw-w-full",
            isVisible ? "tw-opacity-100" : "tw-opacity-0",
            "tw-transition-opacity tw-duration-200",
          )}
          style={{
            minHeight: isVisible ? "auto" : "300px",
            maxWidth: "28rem",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

const DesktopWrapper = ({
  children,
  progressBar,
}: {
  children: React.ReactNode;
  progressBar: React.ReactNode;
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // this is used to fade in the container,
    // to help prevent layout shift when running an experiment which often causes a flicker
    // when the variant loads
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={cn(
        "tw-relative",
        "tw-bg-gray-100",
        "tw-bg-gradient-to-tl tw-from-gray-50 tw-via-gray-100 tw-to-gray-100",
        "tw-bg-center tw-bg-no-repeat tw-bg-cover",
        "tw-min-h-screen tw-pb-12 tw-flex tw-flex-col tw-items-center tw-justify-start md:tw-justify-center tw-w-full",
      )}
    >
      <div
        className={cn(
          "tw-absolute tw-inset-0 tw-z-0",
          "tw-bg-repeat",
          "tw-pointer-events-none",
        )}
        style={{
          backgroundImage: `url(${BackgroundPattern})`,
          backgroundSize: "200px 200px",
        }}
      />
      <div>
        <div className="tw-py-8 tw-flex tw-items-start tw-justify-start tw-space-y-4">
          <Logo className="tw-h-10" />
        </div>

        <div className="tw-relative tw-z-10 tw-flex tw-flex-col tw-justify-center tw-items-center tw-w-full tw-h-full tw-my-auto">
          <div className="tw-max-w-md tw-w-full tw-mb-4">{progressBar}</div>
          <div
            className={cn(
              "tw-rounded-lg tw-h-full tw-bg-white sm:tw-w-full tw-flex tw-flex-col tw-justify-center",
              "tw-max-w-md tw-p-12 md:tw-shadow-md",
              "tw-border",
              isVisible ? "tw-opacity-100" : "tw-opacity-0",
              "tw-transition-opacity tw-duration-200",
              "tw-min-w-[20rem]",
            )}
            style={{
              minHeight: isVisible ? "auto" : "300px",
              width: "100%",
              maxWidth: "28rem",
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const SignUpControlVersion = ({ progressBar, isDesktopView }) => {
  const [state, send, service] = useMachine(signupMachineControl);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const utmsFromUrl = getAttributionFromUrl(window.location.href);
      const hasUtmsInUrl = Object.values(utmsFromUrl).some(Boolean);
      if (hasUtmsInUrl) {
        setUtmCookies(utmsFromUrl);
        send({ type: "SET_UTM", utm: utmsFromUrl });
      } else {
        const utmsFromCookies = getAttributionFromCookies();
        send({ type: "SET_UTM", utm: utmsFromCookies });
      }
    }
  }, [send]);

  return (
    <SignUpMachineContext.Provider value={service}>
      {isDesktopView ? (
        <DesktopWrapper progressBar={progressBar}>
          <SignupFlowContainer machineState={state} machineSend={send} />
        </DesktopWrapper>
      ) : (
        <MobileWrapper progressBar={progressBar}>
          <SignupFlowContainer machineState={state} machineSend={send} />
        </MobileWrapper>
      )}
    </SignUpMachineContext.Provider>
  );
};

const SignUpVariantVersion = ({ progressBar, isDesktopView }) => {
  const [state, send, service] = useMachine(signupMachineControl);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const utmsFromUrl = getAttributionFromUrl(window.location.href);
      const hasUtmsInUrl = Object.values(utmsFromUrl).some(Boolean);
      if (hasUtmsInUrl) {
        setUtmCookies(utmsFromUrl);
        send({ type: "SET_UTM", utm: utmsFromUrl });
      } else {
        const utmsFromCookies = getAttributionFromCookies();
        send({ type: "SET_UTM", utm: utmsFromCookies });
      }
    }
  }, [send]);

  return (
    <SignUpMachineContext.Provider value={service}>
      {isDesktopView ? (
        <DesktopWrapper progressBar={progressBar}>
          <SignupFlowContainer machineState={state} machineSend={send} />
        </DesktopWrapper>
      ) : (
        <MobileWrapper progressBar={progressBar}>
          <SignupFlowContainer machineState={state} machineSend={send} />
        </MobileWrapper>
      )}
    </SignUpMachineContext.Provider>
  );
};

const SignUpLanding = () => {
  const isDesktopView = useTailwindBreakpoint().isSmScreen;
  const GOOGLE_TAG_MANAGER_ID = process.env.SAF_GOOGLE_TAG_MANAGER_ID;
  const isGoogleAnalyticsEnabled = isProd() && GOOGLE_TAG_MANAGER_ID;

  const { useExperimentIsOn } = useExperiment();
  const isExperimentVariantOn = useExperimentIsOn("signup-flow-experiment");

  return (
    <>
      <Helmet>
        <title>
          Sign up | Complete Gym Management Platform | PushPress.com
        </title>
        {isGoogleAnalyticsEnabled && (
          <script>
            {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${GOOGLE_TAG_MANAGER_ID}');`}
          </script>
        )}
      </Helmet>
      {isExperimentVariantOn ? (
        <SignUpVariantVersion
          progressBar={<ProgressBar />}
          isDesktopView={isDesktopView}
        />
      ) : (
        <SignUpControlVersion
          progressBar={<></>}
          isDesktopView={isDesktopView}
        />
      )}
    </>
  );
};

export default SignUpLanding;
